import { useEffect, useState } from "react";
import { ownerApi, formatDetail } from "../api";
import { formatCT } from "../lib/time";
import { Loader2, Save } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const FEATURE_KEYS = [
  ["learner_management", "Learner Management"],
  ["events", "Events"],
  ["lesson_planner", "Lesson Planner"],
  ["resource_library", "Resource Library"],
  ["curriculum", "Curriculum Management"],
  ["question_bank", "Question Bank"],
  ["historical_assessments", "Historical Assessments"],
  ["gradebook", "Gradebook"],
  ["surveys", "Surveys"],
  ["analytics", "Analytics"],
  ["learning_logic", "Learning Logic"],
  ["epic_practice", "Sandbox"],
];

export default function PlatformSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await ownerApi.get("/platform-settings");
      setData(data);
    } catch { setData({ error: true }); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>;
  if (data.error) return <p className="text-red-400">Failed to load settings</p>;

  const togglePresetFlag = (tier, key) => {
    setData({
      ...data,
      feature_presets: {
        ...data.feature_presets,
        [tier]: { ...data.feature_presets[tier], [key]: !data.feature_presets[tier][key] },
      },
    });
  };

  const setGlobal = (key, val) => setData({ ...data, global_settings: { ...data.global_settings, [key]: val } });

  const save = async () => {
    setSaving(true);
    try {
      await ownerApi.put("/platform-settings", {
        feature_presets: data.feature_presets,
        global_settings: data.global_settings,
      });
      toast.success("Platform settings saved");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="owner-platform-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Platform Settings</h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1">Feature tier presets and platform-wide defaults.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#10b7c0] hover:bg-[#0ea2aa] disabled:opacity-50 text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5"
          data-testid="owner-platform-save"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save changes
        </button>
      </div>

      {/* Feature preset matrix */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5">
        <h2 className="font-heading font-bold text-sm mb-4">Feature tier presets</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="owner-platform-presets">
            <thead className="text-[10px] uppercase tracking-wider text-[#ebeef5]/70">
              <tr>
                <th className="text-left py-2 font-semibold">Feature</th>
                <th className="text-center py-2 font-semibold w-24">Level I</th>
                <th className="text-center py-2 font-semibold w-24">Level II</th>
                <th className="text-center py-2 font-semibold w-24">Level III</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {FEATURE_KEYS.map(([key, label]) => (
                <tr key={key}>
                  <td className="py-2 text-[#ebeef5]">{label}</td>
                  {["level_1", "level_2", "level_3"].map((tier) => (
                    <td key={tier} className="py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!!data.feature_presets?.[tier]?.[key]}
                        onChange={() => togglePresetFlag(tier, key)}
                        className="accent-[#10b7c0] h-4 w-4 cursor-pointer"
                        data-testid={`preset-${tier}-${key}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Global settings */}
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-5">
        <h2 className="font-heading font-bold text-sm mb-4">Global defaults</h2>
        <p className="text-xs text-[#ebeef5]/70 mb-4">Fallback values applied when an org has no per-org branding set.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SField label="App title" value={data.global_settings?.app_title || ""} onChange={(v) => setGlobal("app_title", v)} testid="gs-title" />
          <SField label="Primary color" value={data.global_settings?.primary_color || ""} onChange={(v) => setGlobal("primary_color", v)} testid="gs-primary" />
          <SField label="Secondary color" value={data.global_settings?.secondary_color || ""} onChange={(v) => setGlobal("secondary_color", v)} testid="gs-secondary" />
          <SField label="Logo URL" value={data.global_settings?.logo_url || ""} onChange={(v) => setGlobal("logo_url", v)} testid="gs-logo" />
        </div>
      </div>

      <p className="text-[11px] text-[#ebeef5]/50 text-right">
        Last updated {data.updated_at ? formatCT(data.updated_at) : "—"}
      </p>
    </div>
  );
}

function SField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] focus:outline-none focus:border-[#10b7c0]/50"
      />
    </div>
  );
}
